import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Lock, Layers, Code2, Box, Wand2, Film, Bot, Zap, Cpu, Microscope, Network, UserCircle, Phone, Brain, Video, Camera, Megaphone, Globe } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SERVICES_COMING_SOON, PRICING_TIERS, CONTACT } from "../data/services";
import { api } from "../lib/api";
import { useSiteContent, imgSrc } from "../lib/siteContent";

const CATEGORY_ICONS = {
  "Creative & Design": Wand2,
  "Web & Software": Code2,
  "AI & Intelligence": Brain,
  "Media & Marketing": Megaphone,
};

const SERVICE_ICON_MAP = {
  "ppt": Layers, "3d": Box, "templates": Layers, "video-edit": Video, "photo-edit": Camera,
  "software": Code2, "landing": Globe, "website": Globe, "animated": Zap, "workflow": Network,
  "ecosystem": Network, "os": Cpu, "ai-agents": Bot, "ai-avatar": UserCircle, "ai-call": Phone,
  "personal-ai": Brain, "medical-ai": Microscope, "video": Film, "marketing-ads": Megaphone,
};

export default function ServicesPage() {
  const { content } = useSiteContent();
  const [services, setServices] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/services");
        setServices(data);
      } catch (e) {
        // page still renders
      }
    })();
  }, []);

  const categories = ["All", ...Array.from(new Set(services.map((s) => s.category)))];
  const filtered = activeCategory === "All" ? services : services.filter((s) => s.category === activeCategory);
  const sp = content.services_page;

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />

      {/* Header */}
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] bg-[#2060EE]/10 blur-[140px] rounded-full" />
        <div className="absolute -bottom-20 right-1/4 w-[400px] h-[400px] bg-[#8B2FE8]/10 blur-[120px] rounded-full" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">{sp.eyebrow}</p>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tight max-w-4xl">
            {services.length} {sp.title_pre} <span className="text-gradient-cyan">{sp.title_accent}</span>.
          </h1>
          <p className="text-[#5A5A72] mt-6 max-w-2xl">{sp.subtitle}</p>

          {/* Stats strip */}
          <div className="mt-10 flex flex-wrap gap-6">
            {[
              { v: `${services.length}`, l: "Active services" },
              { v: `${SERVICES_COMING_SOON.length}`, l: "Coming soon" },
              { v: `${Math.max(categories.length - 1, 0)}`, l: "Categories" },
            ].map((s) => (
              <div key={s.l} className="glass-card rounded-xl px-5 py-3 flex items-center gap-3">
                <span className="font-display font-black text-2xl text-gradient-cyan">{s.v}</span>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="sticky top-0 z-30 bg-[#F8F8F6]/90 backdrop-blur-md border-b border-black/[0.06]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-none">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat];
              const count = cat === "All" ? services.length : services.filter((s) => s.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`cat-tab-${cat.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wider whitespace-nowrap transition-all ${
                    activeCategory === cat
                      ? "bg-[#00D4FF]/12 border border-[#00D4FF]/40 text-[#00D4FF]"
                      : "border border-black/[0.08] text-[#5A5A72] hover:border-black/20 hover:text-[#0E0E1A]"
                  }`}
                >
                  {Icon && <Icon className="w-3 h-3" />}
                  {cat}
                  <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${activeCategory === cat ? "bg-[#00D4FF]/20 text-[#00D4FF]" : "bg-black/5 text-[#6B7280]"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 xl:grid-cols-3 gap-6"
            >
              {filtered.map((s, i) => {
                const Icon = SERVICE_ICON_MAP[s.id] || Layers;
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.5 }}
                    whileHover={{ y: -4 }}
                    data-testid={`service-card-${s.id}`}
                    className="group glass-card neon-border rounded-2xl overflow-hidden flex flex-col transition-all duration-300"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={imgSrc(s.image)}
                        alt={s.name}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#06060C] via-[#06060C]/50 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00D4FF] bg-[#00D4FF]/10 border border-[#00D4FF]/25 px-2.5 py-1 rounded-full">
                          {s.category}
                        </span>
                      </div>
                      <div className="absolute bottom-4 right-4 w-10 h-10 rounded-xl bg-[#F8F8F6]/80 grid place-items-center border border-black/[0.08]">
                        <Icon className="w-5 h-5 text-[#00D4FF]" />
                      </div>
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <h2 className="font-display font-bold text-xl">{s.name}</h2>
                      <p className="text-xs font-mono text-[#00D4FF] mt-1">{s.short}</p>
                      <p className="text-sm text-[#5A5A72] mt-3 leading-relaxed flex-1">{s.description}</p>

                      <div className="mt-4 grid grid-cols-2 gap-1.5">
                        {(s.deliverables || []).map((d) => (
                          <div key={d} className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                            <Check className="w-3 h-3 text-[#00D4FF] flex-shrink-0" />{d}
                          </div>
                        ))}
                      </div>

                      <Link
                        to="/quote"
                        state={{ service: s.name }}
                        className="mt-5 inline-flex items-center gap-2 btn-ghost px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-wider self-start"
                      >
                        Discuss this <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-violet-400 mb-2">/ on the horizon</p>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight">
              Coming <span className="text-gradient-violet">soon</span>.
            </h2>
            <p className="text-[#6B7280] mt-2 text-sm">These service lines are in active development. Notify us if you're interested.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {SERVICES_COMING_SOON.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`coming-soon-${s.id}`}
                className="relative glass-card rounded-2xl p-6 overflow-hidden border border-violet-400/10 opacity-70 hover:opacity-90 transition-opacity"
              >
                <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-violet-300 bg-violet-400/10 border border-violet-400/20 px-2.5 py-1 rounded-full">
                  <Lock className="w-2.5 h-2.5" /> Coming Soon
                </div>
                <div className="w-10 h-10 rounded-xl bg-violet-400/10 grid place-items-center border border-violet-400/20 mb-4">
                  <Lock className="w-4 h-4 text-violet-300" />
                </div>
                <h3 className="font-display font-bold text-xl">{s.name}</h3>
                <p className="text-xs font-mono text-violet-400 mt-1">{s.short}</p>
                <p className="text-sm text-[#6B7280] mt-3 leading-relaxed">{s.description}</p>
                <Link to="/quote" className="mt-5 inline-flex items-center gap-2 text-xs font-mono text-violet-400 hover:text-violet-300 uppercase tracking-wider">
                  Notify me <ArrowUpRight className="w-3 h-3" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      <section className="pb-24 border-t border-black/[0.06]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ engagement tiers</p>
          <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tight">Three lanes.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PRICING_TIERS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`glass-card rounded-2xl p-7 relative ${t.highlight ? "border-[#00D4FF]/35 glow-cyan" : ""}`}
              >
                {t.highlight && (
                  <span className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-[0.2em] text-[#00D4FF] bg-[#00D4FF]/10 px-2.5 py-1 rounded-full">
                    Popular
                  </span>
                )}
                <h3 className="font-display font-bold text-2xl">{t.name}</h3>
                <p className="text-sm text-[#5A5A72] mt-1">{t.tagline}</p>
                <div className="mt-5">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00D4FF]">Pricing</p>
                  <p className="font-display font-black text-xl mt-1 text-gradient-cyan">Talk to us</p>
                </div>
                <ul className="mt-6 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#4B5563]">
                      <Check className="w-4 h-4 mt-0.5 text-[#00D4FF] flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/quote"
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  className={`mt-7 inline-flex w-full justify-center items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold ${t.highlight ? "btn-primary" : "btn-ghost"}`}
                >
                  Request quote <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-[#5A5A72]">
            For pricing discussions, call{" "}
            <a href={`tel:+91${CONTACT.phone}`} className="text-[#00D4FF] hover:underline font-mono">{CONTACT.phoneDisplay}</a>
            {" "}or email{" "}
            <a href={`mailto:${CONTACT.email}`} className="text-[#00D4FF] hover:underline font-mono">{CONTACT.email}</a>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
